// src/lib/opportunity/salesAdjustmentsValidation.ts
//
// Shared client/server guards for slice 7c sales adjustments + deposit
// deviation notes, plus per-line $/% calc-line override notes.

import type { OpportunityPricingInputs } from "../../types/opportunity.js";
import type { FormatCalcLineDef } from "./formatCalcLineTypes.js";
import { effectiveCalcLineMode } from "./formatCalcEval.js";

export const SALES_ADJUSTMENT_DESCRIPTION_MAX_LEN = 200;
export const DEPOSIT_DEVIATION_NOTE_MAX_LEN = 500;
export const CALC_LINE_DEVIATION_NOTE_MAX_LEN = 500;

function numFromRaw(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.replace(/[$,\s]/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/** Returns an error message when inputs fail 7c validation rules. */
export function validateSalesPricingInputs(
  inputs: OpportunityPricingInputs,
): string | null {
  for (const adj of inputs.sales_adjustments ?? []) {
    const description = (adj.description ?? "").trim();
    const value = numFromRaw(adj.value);
    if (value !== 0 && !description) {
      return "Each subtotal adjustment with an amount requires a description.";
    }
    if (description.length > SALES_ADJUSTMENT_DESCRIPTION_MAX_LEN) {
      return `Adjustment descriptions are limited to ${SALES_ADJUSTMENT_DESCRIPTION_MAX_LEN} characters.`;
    }
  }

  if (inputs.deposit_mode === "amount") {
    const note = (inputs.deposit_deviation_note ?? "").trim();
    if (!note) {
      return "Enter a note explaining the manual deposit amount.";
    }
    if (note.length > DEPOSIT_DEVIATION_NOTE_MAX_LEN) {
      return `Deposit deviation notes are limited to ${DEPOSIT_DEVIATION_NOTE_MAX_LEN} characters.`;
    }
  }

  return null;
}

/**
 * Guards for per-line $/% overrides on manager calc lines. Forcing a
 * percent-defined line to exact dollars requires a note explaining the
 * deviation (mirrors the manual-deposit rule). Fixed lines edited in
 * dollars are the normal path and need no note.
 */
export function validateFormatCalcOverrides(
  inputs: OpportunityPricingInputs,
  calcLines: readonly FormatCalcLineDef[],
): string | null {
  const modes = inputs.format_calc_modes ?? {};
  const notes = inputs.format_calc_deviation_notes ?? {};
  for (const line of calcLines) {
    const isOverriddenToDollars =
      line.amount_mode === "percent" &&
      effectiveCalcLineMode(line, modes) === "fixed";
    if (!isOverriddenToDollars) continue;
    const note = (notes[line.id] ?? "").trim();
    if (!note) {
      return `"${line.label}" is overridden to a dollar amount — enter a note explaining why.`;
    }
    if (note.length > CALC_LINE_DEVIATION_NOTE_MAX_LEN) {
      return `Line override notes are limited to ${CALC_LINE_DEVIATION_NOTE_MAX_LEN} characters.`;
    }
  }
  return null;
}
